import { useState, useRef, useEffect } from 'react';
import {
  Play,
  Save,
  Download,
  Upload,
  Trash2,
  Settings,
  X,
  Zap,
  Database,
  Filter,
  CheckCircle,
  FileText,
  Brain,
  Search,
  Shuffle,
  AlertCircle,
  BookOpen,
  Layers,
  CheckCircle2,
  XCircle,
  Info,
  AlertTriangle,
} from 'lucide-react';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Tooltip } from '@/components/Tooltip';
import { StatusBadge } from '@/components/Badge';
import { confirmDelete, showDeleteSuccess } from '@/utils/deleteConfirmation';

interface Node {
  id: string;
  type: string;
  label: string;
  x: number;
  y: number;
  config: Record<string, any>;
}

interface Connection {
  id: string;
  from: string;
  to: string;
}

interface WorkflowNodeType {
  type: string;
  label: string;
  icon: any;
  color: string;
  category: 'input' | 'processing' | 'validation' | 'output';
  description: string;
  defaultConfig: Record<string, any>;
}

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  examType: string;
  useCase: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  nodes: Node[];
  connections: Connection[];
}

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
}

export function WorkflowBuilder() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [draggingNode, setDraggingNode] = useState<Node | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [isExecuting, setIsExecuting] = useState(false);
  const [showNodeConfig, setShowNodeConfig] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const toastTimersRef = useRef<Map<string, number>>(new Map());

  const nodeTypes: WorkflowNodeType[] = [
    {
      type: 'input-exam',
      label: 'Exam Input',
      icon: FileText,
      color: 'blue',
      category: 'input',
      description: 'Define exam type and parameters',
      defaultConfig: {
        examType: 'CPA-10',
        topicId: '',
        difficulty: 'medium',
        questionCount: 1,
        questionType: 'multiple-choice', // New: multiple-choice, true-false, essay
        schedule: '', // New: Schedule execution (cron format or date)
        tags: [], // New: Tags for categorization
        bloomLevel: 'understand', // New: remember, understand, apply, analyze, evaluate, create
        timeLimit: 0, // New: Time limit per question in minutes (0 = no limit)
        passingScore: 70, // New: Passing percentage
        randomizeOptions: true, // New: Randomize answer options
        showExplanations: true, // New: Include explanations
        courseName: '', // New: Associated course
      },
    },
    {
      type: 'input-topic',
      label: 'Topic Selector',
      icon: Search,
      color: 'cyan',
      category: 'input',
      description: 'Select specific topics or OE codes',
      defaultConfig: {
        oeCode: '',
        topics: [],
        depth: 1,
      },
    },
    {
      type: 'query-analyzer',
      label: 'Query Analyzer',
      icon: Brain,
      color: 'purple',
      category: 'processing',
      description: 'Analyze query with Claude agent',
      defaultConfig: {
        model: 'claude-sonnet-4.5',
        temperature: 0.3,
        extractEntities: true,
      },
    },
    {
      type: 'mcp-router',
      label: 'MCP Router',
      icon: Database,
      color: 'indigo',
      category: 'processing',
      description: 'Select and query MCP servers',
      defaultConfig: {
        servers: ['Pinecone', 'OE Database', 'Regulations'],
        parallel: true,
        timeout: 5000,
      },
    },
    {
      type: 'reranker',
      label: 'Reranker',
      icon: Shuffle,
      color: 'pink',
      category: 'processing',
      description: 'Rerank and deduplicate results',
      defaultConfig: {
        model: 'cohere-rerank',
        topK: 15,
        deduplicate: true,
        threshold: 0.9,
      },
    },
    {
      type: 'llm-generator',
      label: 'LLM Generator',
      icon: Zap,
      color: 'yellow',
      category: 'processing',
      description: 'Generate questions with LLM',
      defaultConfig: {
        model: 'claude-sonnet-4.5',
        temperature: 0.7,
        maxTokens: 4000,
        systemPrompt: '',
      },
    },
    {
      type: 'filter',
      label: 'Filter',
      icon: Filter,
      color: 'orange',
      category: 'processing',
      description: 'Filter questions by criteria',
      defaultConfig: {
        minQuality: 0.8,
        requireOEAlignment: true,
        excludeKeywords: [],
      },
    },
    {
      type: 'validation',
      label: 'Validation',
      icon: CheckCircle,
      color: 'green',
      category: 'validation',
      description: 'Validate question quality',
      defaultConfig: {
        layers: ['technical', 'factual', 'oe-alignment', 'difficulty'],
        minConfidence: 0.85,
        autoFix: false,
      },
    },
    {
      type: 'output-save',
      label: 'Save Questions',
      icon: Database,
      color: 'emerald',
      category: 'output',
      description: 'Save to question bank',
      defaultConfig: {
        status: 'under_review',
        autoApprove: false,
        notifyReviewers: true,
      },
    },
    {
      type: 'output-export',
      label: 'Export',
      icon: Download,
      color: 'teal',
      category: 'output',
      description: 'Export questions to file',
      defaultConfig: {
        format: 'json',
        includeMetadata: true,
        filename: 'questions_export',
      },
    },
  ];

  // Comprehensive Template Library for all Exam Types
  const workflowTemplates: WorkflowTemplate[] = [
    // CPA-10 Templates
    {
      id: 'cpa10-quick-test',
      name: 'CPA-10: Quick Test (1 Question)',
      description: 'Fast testing workflow - generates 1 question without database save. Perfect for testing.',
      examType: 'CPA-10',
      useCase: 'Quick Testing',
      difficulty: 'beginner',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CPA-10', topicId: '', difficulty: 'easy', questionCount: 1 } },
        { id: 'n2', type: 'llm-generator', label: 'LLM Generator', x: 350, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 3000, systemPrompt: '' } },
        { id: 'n3', type: 'output-export', label: 'Export', x: 650, y: 150, config: { format: 'json', includeMetadata: true, filename: 'test_question' } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
      ],
    },
    {
      id: 'cpa10-production',
      name: 'CPA-10: Production Quality (Full Pipeline)',
      description: 'Complete production workflow with query analysis, MCP routing, reranking, and validation. Best for high-quality questions.',
      examType: 'CPA-10',
      useCase: 'Production',
      difficulty: 'advanced',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CPA-10', topicId: '', difficulty: 'medium', questionCount: 5 } },
        { id: 'n2', type: 'query-analyzer', label: 'Query Analyzer', x: 250, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.3, extractEntities: true } },
        { id: 'n3', type: 'mcp-router', label: 'MCP Router', x: 450, y: 150, config: { servers: ['Pinecone', 'OE Database', 'Regulations', 'Previous Exams'], parallel: true, timeout: 5000 } },
        { id: 'n4', type: 'reranker', label: 'Reranker', x: 650, y: 150, config: { model: 'cohere-rerank', topK: 10, minScore: 0.7 } },
        { id: 'n5', type: 'llm-generator', label: 'LLM Generator', x: 850, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 4000, systemPrompt: '' } },
        { id: 'n6', type: 'filter', label: 'Filter', x: 1050, y: 150, config: { minQuality: 0.85, requireOEAlignment: true, excludeKeywords: [] } },
        { id: 'n7', type: 'validation', label: 'Validation', x: 1250, y: 150, config: { layers: ['technical', 'factual', 'oe-alignment', 'difficulty'], minConfidence: 0.90, autoFix: false } },
        { id: 'n8', type: 'output-save', label: 'Save Questions', x: 1450, y: 150, config: { status: 'under_review', autoApprove: false, notifyReviewers: true } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
        { id: 'c3', from: 'n3', to: 'n4' },
        { id: 'c4', from: 'n4', to: 'n5' },
        { id: 'c5', from: 'n5', to: 'n6' },
        { id: 'c6', from: 'n6', to: 'n7' },
        { id: 'c7', from: 'n7', to: 'n8' },
      ],
    },
    {
      id: 'cpa10-bulk',
      name: 'CPA-10: Bulk Generation (50 Questions)',
      description: 'Generate many questions quickly as drafts for later review. Saves time by skipping validation.',
      examType: 'CPA-10',
      useCase: 'Bulk Generation',
      difficulty: 'intermediate',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CPA-10', topicId: '', difficulty: 'medium', questionCount: 50 } },
        { id: 'n2', type: 'query-analyzer', label: 'Query Analyzer', x: 300, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.3, extractEntities: true } },
        { id: 'n3', type: 'mcp-router', label: 'MCP Router', x: 550, y: 150, config: { servers: ['Pinecone', 'OE Database'], parallel: true, timeout: 5000 } },
        { id: 'n4', type: 'llm-generator', label: 'LLM Generator', x: 800, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 4000, systemPrompt: '' } },
        { id: 'n5', type: 'output-save', label: 'Save Questions', x: 1050, y: 150, config: { status: 'draft', autoApprove: false, notifyReviewers: false } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
        { id: 'c3', from: 'n3', to: 'n4' },
        { id: 'c4', from: 'n4', to: 'n5' },
      ],
    },
    {
      id: 'cpa10-simple',
      name: 'CPA-10: Simple (5 Questions)',
      description: 'Basic workflow for everyday use - query analysis, MCP routing, generation, and save.',
      examType: 'CPA-10',
      useCase: 'Standard Generation',
      difficulty: 'beginner',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CPA-10', topicId: '', difficulty: 'medium', questionCount: 5 } },
        { id: 'n2', type: 'query-analyzer', label: 'Query Analyzer', x: 300, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.3, extractEntities: true } },
        { id: 'n3', type: 'mcp-router', label: 'MCP Router', x: 550, y: 150, config: { servers: ['Pinecone', 'OE Database', 'Regulations'], parallel: true, timeout: 5000 } },
        { id: 'n4', type: 'llm-generator', label: 'LLM Generator', x: 800, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 4000, systemPrompt: '' } },
        { id: 'n5', type: 'output-save', label: 'Save Questions', x: 1050, y: 150, config: { status: 'under_review', autoApprove: false, notifyReviewers: true } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
        { id: 'c3', from: 'n3', to: 'n4' },
        { id: 'c4', from: 'n4', to: 'n5' },
      ],
    },

    // CPA-20 Templates
    {
      id: 'cpa20-quick-test',
      name: 'CPA-20: Quick Test (1 Question)',
      description: 'Fast testing workflow for CPA-20 certification - generates 1 question without database save.',
      examType: 'CPA-20',
      useCase: 'Quick Testing',
      difficulty: 'beginner',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CPA-20', topicId: '', difficulty: 'medium', questionCount: 1 } },
        { id: 'n2', type: 'llm-generator', label: 'LLM Generator', x: 350, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 3000, systemPrompt: '' } },
        { id: 'n3', type: 'output-export', label: 'Export', x: 650, y: 150, config: { format: 'json', includeMetadata: true, filename: 'cpa20_test' } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
      ],
    },
    {
      id: 'cpa20-production',
      name: 'CPA-20: Production Quality (Full Pipeline)',
      description: 'Complete production workflow for CPA-20 with full validation and quality checks.',
      examType: 'CPA-20',
      useCase: 'Production',
      difficulty: 'advanced',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CPA-20', topicId: '', difficulty: 'hard', questionCount: 5 } },
        { id: 'n2', type: 'query-analyzer', label: 'Query Analyzer', x: 250, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.3, extractEntities: true } },
        { id: 'n3', type: 'mcp-router', label: 'MCP Router', x: 450, y: 150, config: { servers: ['Pinecone', 'OE Database', 'Regulations', 'Previous Exams', 'ANBIMA API'], parallel: true, timeout: 5000 } },
        { id: 'n4', type: 'reranker', label: 'Reranker', x: 650, y: 150, config: { model: 'cohere-rerank', topK: 10, minScore: 0.75 } },
        { id: 'n5', type: 'llm-generator', label: 'LLM Generator', x: 850, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.6, maxTokens: 4000, systemPrompt: '' } },
        { id: 'n6', type: 'filter', label: 'Filter', x: 1050, y: 150, config: { minQuality: 0.88, requireOEAlignment: true, excludeKeywords: [] } },
        { id: 'n7', type: 'validation', label: 'Validation', x: 1250, y: 150, config: { layers: ['technical', 'factual', 'oe-alignment', 'difficulty'], minConfidence: 0.92, autoFix: false } },
        { id: 'n8', type: 'output-save', label: 'Save Questions', x: 1450, y: 150, config: { status: 'under_review', autoApprove: false, notifyReviewers: true } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
        { id: 'c3', from: 'n3', to: 'n4' },
        { id: 'c4', from: 'n4', to: 'n5' },
        { id: 'c5', from: 'n5', to: 'n6' },
        { id: 'c6', from: 'n6', to: 'n7' },
        { id: 'c7', from: 'n7', to: 'n8' },
      ],
    },
    {
      id: 'cpa20-bulk',
      name: 'CPA-20: Bulk Generation (30 Questions)',
      description: 'Generate many CPA-20 questions quickly as drafts for later review.',
      examType: 'CPA-20',
      useCase: 'Bulk Generation',
      difficulty: 'intermediate',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CPA-20', topicId: '', difficulty: 'medium', questionCount: 30 } },
        { id: 'n2', type: 'query-analyzer', label: 'Query Analyzer', x: 300, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.3, extractEntities: true } },
        { id: 'n3', type: 'mcp-router', label: 'MCP Router', x: 550, y: 150, config: { servers: ['Pinecone', 'OE Database', 'ANBIMA API'], parallel: true, timeout: 5000 } },
        { id: 'n4', type: 'llm-generator', label: 'LLM Generator', x: 800, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 4000, systemPrompt: '' } },
        { id: 'n5', type: 'output-save', label: 'Save Questions', x: 1050, y: 150, config: { status: 'draft', autoApprove: false, notifyReviewers: false } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
        { id: 'c3', from: 'n3', to: 'n4' },
        { id: 'c4', from: 'n4', to: 'n5' },
      ],
    },
    {
      id: 'cpa20-simple',
      name: 'CPA-20: Simple (5 Questions)',
      description: 'Basic CPA-20 workflow for everyday use with standard quality.',
      examType: 'CPA-20',
      useCase: 'Standard Generation',
      difficulty: 'beginner',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CPA-20', topicId: '', difficulty: 'medium', questionCount: 5 } },
        { id: 'n2', type: 'query-analyzer', label: 'Query Analyzer', x: 300, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.3, extractEntities: true } },
        { id: 'n3', type: 'mcp-router', label: 'MCP Router', x: 550, y: 150, config: { servers: ['Pinecone', 'OE Database', 'Regulations'], parallel: true, timeout: 5000 } },
        { id: 'n4', type: 'llm-generator', label: 'LLM Generator', x: 800, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 4000, systemPrompt: '' } },
        { id: 'n5', type: 'output-save', label: 'Save Questions', x: 1050, y: 150, config: { status: 'under_review', autoApprove: false, notifyReviewers: true } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
        { id: 'c3', from: 'n3', to: 'n4' },
        { id: 'c4', from: 'n4', to: 'n5' },
      ],
    },

    // CEA Templates
    {
      id: 'cea-quick-test',
      name: 'CEA: Quick Test (1 Question)',
      description: 'Fast testing workflow for CEA certification - generates 1 question without database save.',
      examType: 'CEA',
      useCase: 'Quick Testing',
      difficulty: 'beginner',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CEA', topicId: '', difficulty: 'medium', questionCount: 1 } },
        { id: 'n2', type: 'llm-generator', label: 'LLM Generator', x: 350, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 3000, systemPrompt: '' } },
        { id: 'n3', type: 'output-export', label: 'Export', x: 650, y: 150, config: { format: 'json', includeMetadata: true, filename: 'cea_test' } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
      ],
    },
    {
      id: 'cea-production',
      name: 'CEA: Production Quality (Full Pipeline)',
      description: 'Complete production workflow for CEA with full validation and quality checks.',
      examType: 'CEA',
      useCase: 'Production',
      difficulty: 'advanced',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CEA', topicId: '', difficulty: 'hard', questionCount: 5 } },
        { id: 'n2', type: 'query-analyzer', label: 'Query Analyzer', x: 250, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.3, extractEntities: true } },
        { id: 'n3', type: 'mcp-router', label: 'MCP Router', x: 450, y: 150, config: { servers: ['Pinecone', 'OE Database', 'Regulations', 'Previous Exams', 'ANBIMA API'], parallel: true, timeout: 5000 } },
        { id: 'n4', type: 'reranker', label: 'Reranker', x: 650, y: 150, config: { model: 'cohere-rerank', topK: 10, minScore: 0.8 } },
        { id: 'n5', type: 'llm-generator', label: 'LLM Generator', x: 850, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.6, maxTokens: 4000, systemPrompt: '' } },
        { id: 'n6', type: 'filter', label: 'Filter', x: 1050, y: 150, config: { minQuality: 0.90, requireOEAlignment: true, excludeKeywords: [] } },
        { id: 'n7', type: 'validation', label: 'Validation', x: 1250, y: 150, config: { layers: ['technical', 'factual', 'oe-alignment', 'difficulty'], minConfidence: 0.93, autoFix: false } },
        { id: 'n8', type: 'output-save', label: 'Save Questions', x: 1450, y: 150, config: { status: 'under_review', autoApprove: false, notifyReviewers: true } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
        { id: 'c3', from: 'n3', to: 'n4' },
        { id: 'c4', from: 'n4', to: 'n5' },
        { id: 'c5', from: 'n5', to: 'n6' },
        { id: 'c6', from: 'n6', to: 'n7' },
        { id: 'c7', from: 'n7', to: 'n8' },
      ],
    },
    {
      id: 'cea-bulk',
      name: 'CEA: Bulk Generation (40 Questions)',
      description: 'Generate many CEA questions quickly as drafts for later review.',
      examType: 'CEA',
      useCase: 'Bulk Generation',
      difficulty: 'intermediate',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CEA', topicId: '', difficulty: 'medium', questionCount: 40 } },
        { id: 'n2', type: 'query-analyzer', label: 'Query Analyzer', x: 300, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.3, extractEntities: true } },
        { id: 'n3', type: 'mcp-router', label: 'MCP Router', x: 550, y: 150, config: { servers: ['Pinecone', 'OE Database', 'ANBIMA API'], parallel: true, timeout: 5000 } },
        { id: 'n4', type: 'llm-generator', label: 'LLM Generator', x: 800, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 4000, systemPrompt: '' } },
        { id: 'n5', type: 'output-save', label: 'Save Questions', x: 1050, y: 150, config: { status: 'draft', autoApprove: false, notifyReviewers: false } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
        { id: 'c3', from: 'n3', to: 'n4' },
        { id: 'c4', from: 'n4', to: 'n5' },
      ],
    },
    {
      id: 'cea-simple',
      name: 'CEA: Simple (5 Questions)',
      description: 'Basic CEA workflow for everyday use with standard quality.',
      examType: 'CEA',
      useCase: 'Standard Generation',
      difficulty: 'beginner',
      nodes: [
        { id: 'n1', type: 'input-exam', label: 'Exam Input', x: 50, y: 150, config: { examType: 'CEA', topicId: '', difficulty: 'medium', questionCount: 5 } },
        { id: 'n2', type: 'query-analyzer', label: 'Query Analyzer', x: 300, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.3, extractEntities: true } },
        { id: 'n3', type: 'mcp-router', label: 'MCP Router', x: 550, y: 150, config: { servers: ['Pinecone', 'OE Database', 'Regulations'], parallel: true, timeout: 5000 } },
        { id: 'n4', type: 'llm-generator', label: 'LLM Generator', x: 800, y: 150, config: { model: 'claude-sonnet-4.5', temperature: 0.7, maxTokens: 4000, systemPrompt: '' } },
        { id: 'n5', type: 'output-save', label: 'Save Questions', x: 1050, y: 150, config: { status: 'under_review', autoApprove: false, notifyReviewers: true } },
      ],
      connections: [
        { id: 'c1', from: 'n1', to: 'n2' },
        { id: 'c2', from: 'n2', to: 'n3' },
        { id: 'c3', from: 'n3', to: 'n4' },
        { id: 'c4', from: 'n4', to: 'n5' },
      ],
    },
  ];

  // Cleanup toast timers on unmount
  useEffect(() => {
    return () => {
      toastTimersRef.current.forEach(timer => clearTimeout(timer));
      toastTimersRef.current.clear();
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete key - delete selected node
      if (e.key === 'Delete' && selectedNode && !showNodeConfig && !showTemplates) {
        e.preventDefault();
        handleDeleteNode(selectedNode.id);
      }

      // Escape key - deselect node or close modals
      if (e.key === 'Escape') {
        e.preventDefault();
        if (showNodeConfig) {
          setShowNodeConfig(false);
        } else if (showTemplates) {
          setShowTemplates(false);
        } else if (selectedNode) {
          setSelectedNode(null);
        }
      }

      // Ctrl/Cmd + Z - Undo (placeholder for now)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        // TODO: Implement undo functionality
        showToast('info', 'Undo', 'Undo functionality coming soon!');
      }

      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y - Redo (placeholder for now)
      if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') || ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
        e.preventDefault();
        // TODO: Implement redo functionality
        showToast('info', 'Redo', 'Redo functionality coming soon!');
      }

      // Ctrl/Cmd + S - Save workflow
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (nodes.length > 0) {
          handleSaveWorkflow();
        }
      }

      // Ctrl/Cmd + E - Execute workflow
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        if (nodes.length > 0 && !isExecuting) {
          handleExecuteWorkflow();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, showNodeConfig, showTemplates, nodes, isExecuting]);

  // Toast notification system
  const showToast = (type: 'success' | 'error' | 'info' | 'warning', title: string, message?: string) => {
    const id = `toast_${Date.now()}`;
    const newToast: Toast = { id, type, title, message };
    setToasts(prev => [...prev, newToast]);

    // Auto-dismiss after 5 seconds and track timer
    const timer = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      toastTimersRef.current.delete(id);
    }, 5000);
    toastTimersRef.current.set(id, timer);
  };

  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = toastTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimersRef.current.delete(id);
    }
  };

  const handleDragStart = (nodeType: WorkflowNodeType) => {
    // Store node type for drop
    const newNode: Node = {
      id: `node_${Date.now()}`,
      type: nodeType.type,
      label: nodeType.label,
      x: 0,
      y: 0,
      config: { ...nodeType.defaultConfig },
    };
    setDraggingNode(newNode);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggingNode || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newNode = {
      ...draggingNode,
      x: Math.max(0, x - 75), // Center the node
      y: Math.max(0, y - 40),
    };

    setNodes([...nodes, newNode]);
    setDraggingNode(null);
  };

  const handleNodeDragStart = (node: Node, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNode(node);
    setIsDragging(true);

    // Calculate offset from node position to click position
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) return;

    setDragOffset({
      x: e.clientX - canvasRect.left - node.x,
      y: e.clientY - canvasRect.top - node.y
    });
  };

  const handleNodeDrag = (e: React.MouseEvent) => {
    if (!isDragging || !selectedNode || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const newX = Math.max(0, e.clientX - canvasRect.left - dragOffset.x);
    const newY = Math.max(0, e.clientY - canvasRect.top - dragOffset.y);

    setNodes(nodes.map(n =>
      n.id === selectedNode.id
        ? { ...n, x: newX, y: newY }
        : n
    ));
    setSelectedNode({ ...selectedNode, x: newX, y: newY });
  };

  const handleNodeDragEnd = () => {
    setIsDragging(false);
  };

  // Workflow validation
  const validateWorkflow = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (nodes.length === 0) {
      errors.push('Workflow is empty. Please add at least one node.');
      return { valid: false, errors };
    }

    // Check for disconnected nodes
    const connectedNodeIds = new Set<string>();
    connections.forEach(conn => {
      connectedNodeIds.add(conn.from);
      connectedNodeIds.add(conn.to);
    });

    const disconnectedNodes = nodes.filter(n => !connectedNodeIds.has(n.id));
    if (disconnectedNodes.length > 0 && nodes.length > 1) {
      errors.push(`${disconnectedNodes.length} node(s) are disconnected: ${disconnectedNodes.map(n => n.label).join(', ')}`);
    }

    // Check for cycles (simple check)
    const hasCycle = (): boolean => {
      const visited = new Set<string>();
      const recursionStack = new Set<string>();

      const dfs = (nodeId: string): boolean => {
        visited.add(nodeId);
        recursionStack.add(nodeId);

        const outgoingConnections = connections.filter(c => c.from === nodeId);
        for (const conn of outgoingConnections) {
          if (!visited.has(conn.to)) {
            if (dfs(conn.to)) return true;
          } else if (recursionStack.has(conn.to)) {
            return true;
          }
        }

        recursionStack.delete(nodeId);
        return false;
      };

      for (const node of nodes) {
        if (!visited.has(node.id)) {
          if (dfs(node.id)) return true;
        }
      }
      return false;
    };

    if (hasCycle()) {
      errors.push('Workflow contains a cycle. Workflows must be acyclic (no loops).');
    }

    // Check for start and end nodes
    const hasInputNode = nodes.some(n => {
      const typeInfo = getNodeTypeInfo(n.type);
      return typeInfo?.category === 'input';
    });

    const hasOutputNode = nodes.some(n => {
      const typeInfo = getNodeTypeInfo(n.type);
      return typeInfo?.category === 'output';
    });

    if (!hasInputNode) {
      errors.push('Workflow needs at least one Input node (Exam Input or Topic Selector).');
    }

    if (!hasOutputNode) {
      errors.push('Workflow needs at least one Output node (Save Questions or Export).');
    }

    return { valid: errors.length === 0, errors };
  };

  const handleDeleteNode = async (nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    const confirmed = await confirmDelete({
      title: 'Delete Node?',
      itemName: node.label,
    });

    if (confirmed) {
      setNodes(nodes.filter(n => n.id !== nodeId));
      setConnections(connections.filter(c => c.from !== nodeId && c.to !== nodeId));
      if (selectedNode?.id === nodeId) {
        setSelectedNode(null);
      }
      showDeleteSuccess(`"${node.label}" has been removed from the workflow.`);
    }
  };

  const handleConnect = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    if (connections.some(c => c.from === fromId && c.to === toId)) return;

    const newConnection: Connection = {
      id: `conn_${Date.now()}`,
      from: fromId,
      to: toId,
    };
    setConnections([...connections, newConnection]);
  };

  const handleConnectionStart = (nodeId: string) => {
    setConnectingFrom(nodeId);
  };

  const handleConnectionEnd = (nodeId: string) => {
    if (connectingFrom && connectingFrom !== nodeId) {
      handleConnect(connectingFrom, nodeId);
    }
    setConnectingFrom(null);
  };

  const handleDeleteConnection = async (connId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    const connection = connections.find(c => c.id === connId);
    if (!connection) return;

    const fromNode = nodes.find(n => n.id === connection.from);
    const toNode = nodes.find(n => n.id === connection.to);

    const confirmed = await confirmDelete({
      title: 'Delete Connection?',
      itemName: `${fromNode?.label} → ${toNode?.label}`,
    });

    if (confirmed) {
      setConnections(connections.filter(c => c.id !== connId));
      showDeleteSuccess('Connection has been removed.');
    }
  };

  const handleExecuteWorkflow = async () => {
    // Validate workflow before executing
    const validation = validateWorkflow();

    if (!validation.valid) {
      showToast('error', 'Workflow Validation Failed', validation.errors[0]);
      // Show all errors in console for debugging
      console.error('Workflow validation errors:', validation.errors);
      return;
    }

    setIsExecuting(true);
    showToast('info', 'Executing Workflow', 'Processing your question generation pipeline...');

    // Simulate workflow execution
    setTimeout(() => {
      setIsExecuting(false);
      showToast('success', 'Workflow Completed!', '3 questions generated and saved to question bank.');
    }, 3000);
  };

  const handleSaveWorkflow = () => {
    const workflow = {
      name: workflowName,
      nodes,
      connections,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem('workflow_' + Date.now(), JSON.stringify(workflow));
    showToast('success', 'Workflow Saved', `"${workflowName}" has been saved to browser storage.`);
  };

  const handleExportWorkflow = () => {
    const workflow = {
      name: workflowName,
      nodes,
      connections,
      version: '1.0',
      createdAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(workflow, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workflowName.replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportWorkflow = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workflow = JSON.parse(event.target?.result as string);
        setWorkflowName(workflow.name || 'Imported Workflow');
        setNodes(workflow.nodes || []);
        setConnections(workflow.connections || []);
        showToast('success', 'Workflow Imported', 'Your workflow has been loaded successfully.');
      } catch (error) {
        showToast('error', 'Import Failed', 'Please check the file format and try again.');
      }
    };
    reader.readAsText(file);
  };

  const handleLoadExample = () => {
    setWorkflowName('Example: Simple Question Generation');
    const exampleNodes: Node[] = [
      {
        id: 'node_1',
        type: 'input-exam',
        label: 'Exam Input',
        x: 50,
        y: 150,
        config: {
          examType: 'CPA-10',
          topicId: 'CPA_T2',
          difficulty: 'medium',
          questionCount: 3,
        },
      },
      {
        id: 'node_2',
        type: 'query-analyzer',
        label: 'Query Analyzer',
        x: 300,
        y: 150,
        config: {
          model: 'claude-sonnet-4.5',
          temperature: 0.3,
          extractEntities: true,
        },
      },
      {
        id: 'node_3',
        type: 'mcp-router',
        label: 'MCP Router',
        x: 550,
        y: 150,
        config: {
          servers: ['Pinecone', 'OE Database', 'Regulations'],
          parallel: true,
          timeout: 5000,
        },
      },
      {
        id: 'node_4',
        type: 'llm-generator',
        label: 'LLM Generator',
        x: 800,
        y: 150,
        config: {
          model: 'claude-sonnet-4.5',
          temperature: 0.7,
          maxTokens: 4000,
          systemPrompt: '',
        },
      },
      {
        id: 'node_5',
        type: 'output-save',
        label: 'Save Questions',
        x: 1050,
        y: 150,
        config: {
          status: 'under_review',
          autoApprove: false,
          notifyReviewers: true,
        },
      },
    ];

    const exampleConnections: Connection[] = [
      { id: 'conn_1', from: 'node_1', to: 'node_2' },
      { id: 'conn_2', from: 'node_2', to: 'node_3' },
      { id: 'conn_3', from: 'node_3', to: 'node_4' },
      { id: 'conn_4', from: 'node_4', to: 'node_5' },
    ];

    setNodes(exampleNodes);
    setConnections(exampleConnections);
    showToast('success', 'Example Workflow Loaded!', 'Basic pipeline: Exam Input → Query Analyzer → MCP Router → LLM Generator → Save Questions. Click "Execute Workflow" to run it!');
  };

  const handleLoadTemplate = (template: WorkflowTemplate) => {
    setWorkflowName(template.name);
    setNodes(template.nodes);
    setConnections(template.connections);
    setShowTemplates(false);
    showToast('success', `Template Loaded: ${template.name}`, `${template.description} - Ready to execute or customize!`);
  };

  const getNodeTypeInfo = (type: string) => {
    return nodeTypes.find(nt => nt.type === type);
  };

  // Color mapping for Tailwind classes (dynamic classes don't work with JIT)
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600',
    },
    cyan: {
      bg: 'bg-cyan-50',
      border: 'border-cyan-200',
      iconBg: 'bg-cyan-100',
      iconText: 'text-cyan-600',
    },
    purple: {
      bg: 'bg-fk-orange-vibrant50',
      border: 'border-orange-200',
      iconBg: 'bg-fk-orange-vibrant100',
      iconText: 'text-#ea7500',
    },
    indigo: {
      bg: 'bg-fk-blue-primary50',
      border: 'border-fk-blue-primary200',
      iconBg: 'bg-fk-blue-primary100',
      iconText: 'text-#1e73be',
    },
    pink: {
      bg: 'bg-fk-orange-soft50',
      border: 'border-fk-orange-soft200',
      iconBg: 'bg-fk-orange-soft100',
      iconText: 'text-#ea7500',
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      iconBg: 'bg-yellow-100',
      iconText: 'text-yellow-600',
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      iconBg: 'bg-orange-100',
      iconText: 'text-orange-600',
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      iconBg: 'bg-green-100',
      iconText: 'text-green-600',
    },
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      iconBg: 'bg-emerald-100',
      iconText: 'text-emerald-600',
    },
    teal: {
      bg: 'bg-teal-50',
      border: 'border-teal-200',
      iconBg: 'bg-teal-100',
      iconText: 'text-teal-600',
    },
  };

  const renderConnection = (conn: Connection) => {
    const fromNode = nodes.find(n => n.id === conn.from);
    const toNode = nodes.find(n => n.id === conn.to);
    if (!fromNode || !toNode) return null;

    const x1 = fromNode.x + 150;
    const y1 = fromNode.y + 40;
    const x2 = toNode.x;
    const y2 = toNode.y + 40;

    const midX = (x1 + x2) / 2;

    return (
      <g key={conn.id}>
        <path
          d={`M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`}
          stroke="#6366f1"
          strokeWidth="2"
          fill="none"
          className="cursor-pointer hover:stroke-red-500 transition-colors"
          onClick={(e) => handleDeleteConnection(conn.id, e as any)}
        />
        <circle cx={x1} cy={y1} r="4" fill="#6366f1" />
        <circle cx={x2} cy={y2} r="4" fill="#6366f1" />
      </g>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <Breadcrumb items={[
          { label: 'RAG System', path: '/rag' },
          { label: 'Workflow Builder' }
        ]} />

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-4">
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              className="text-2xl font-bold text-gray-900 bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none transition-colors"
            />
            <StatusBadge status={isExecuting ? 'in_progress' : nodes.length > 0 ? 'active' : 'draft'} />
          </div>

          <div className="flex items-center space-x-2">
            <Tooltip content="Load an example workflow to see how it works">
              <button
                onClick={handleLoadExample}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:from-green-600 hover:to-emerald-700 transition-all shadow-md flex items-center"
              >
                <Zap className="w-4 h-4 mr-2" />
                Load Example
              </button>
            </Tooltip>

            <Tooltip content="Browse pre-made templates for all exam types (CPA-10, CPA-20, CEA)">
              <button
                onClick={() => setShowTemplates(true)}
                className="px-4 py-2 bg-gradient-to-r from-#f09628 to-#1e73be text-white rounded-lg text-sm font-medium hover:from-#ea7500 hover:to-#002258 transition-all shadow-md flex items-center"
              >
                <Layers className="w-4 h-4 mr-2" />
                Templates
              </button>
            </Tooltip>

            <Tooltip content="Import workflow from JSON file">
              <label className="btn-secondary flex items-center text-sm cursor-pointer">
                <Upload className="w-4 h-4 mr-2" />
                Import
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportWorkflow}
                  className="hidden"
                />
              </label>
            </Tooltip>

            <Tooltip content="Export workflow as JSON file">
              <button
                onClick={handleExportWorkflow}
                disabled={nodes.length === 0}
                className="btn-secondary flex items-center text-sm disabled:opacity-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </Tooltip>

            <Tooltip content="Save workflow to library">
              <button
                onClick={handleSaveWorkflow}
                disabled={nodes.length === 0}
                className="btn-secondary flex items-center text-sm disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </button>
            </Tooltip>

            <Tooltip content="Execute this workflow to generate questions">
              <button
                onClick={handleExecuteWorkflow}
                disabled={nodes.length === 0 || isExecuting}
                className="btn-primary flex items-center text-sm disabled:opacity-50"
              >
                {isExecuting ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Execute Workflow
                  </>
                )}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Node Palette - Responsive */}
        <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto hidden md:block">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Workflow Nodes
            </h3>

            {/* Input Nodes */}
            <div className="mb-6">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Input Nodes</h4>
              <div className="space-y-2">
                {nodeTypes.filter(nt => nt.category === 'input').map((nodeType) => {
                  const Icon = nodeType.icon;
                  const colors = colorClasses[nodeType.color as keyof typeof colorClasses];
                  return (
                    <Tooltip key={nodeType.type} content={nodeType.description}>
                      <div
                        draggable
                        onDragStart={() => handleDragStart(nodeType)}
                        className={`flex items-center space-x-3 p-3 ${colors.bg} border ${colors.border} rounded-lg cursor-move hover:shadow-md transition-all`}
                      >
                        <div className={`p-2 ${colors.iconBg} rounded`}>
                          <Icon className={`w-4 h-4 ${colors.iconText}`} />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{nodeType.label}</span>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </div>

            {/* Processing Nodes */}
            <div className="mb-6">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Processing Nodes</h4>
              <div className="space-y-2">
                {nodeTypes.filter(nt => nt.category === 'processing').map((nodeType) => {
                  const Icon = nodeType.icon;
                  const colors = colorClasses[nodeType.color as keyof typeof colorClasses];
                  return (
                    <Tooltip key={nodeType.type} content={nodeType.description}>
                      <div
                        draggable
                        onDragStart={() => handleDragStart(nodeType)}
                        className={`flex items-center space-x-3 p-3 ${colors.bg} border ${colors.border} rounded-lg cursor-move hover:shadow-md transition-all`}
                      >
                        <div className={`p-2 ${colors.iconBg} rounded`}>
                          <Icon className={`w-4 h-4 ${colors.iconText}`} />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{nodeType.label}</span>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </div>

            {/* Validation Nodes */}
            <div className="mb-6">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Validation Nodes</h4>
              <div className="space-y-2">
                {nodeTypes.filter(nt => nt.category === 'validation').map((nodeType) => {
                  const Icon = nodeType.icon;
                  const colors = colorClasses[nodeType.color as keyof typeof colorClasses];
                  return (
                    <Tooltip key={nodeType.type} content={nodeType.description}>
                      <div
                        draggable
                        onDragStart={() => handleDragStart(nodeType)}
                        className={`flex items-center space-x-3 p-3 ${colors.bg} border ${colors.border} rounded-lg cursor-move hover:shadow-md transition-all`}
                      >
                        <div className={`p-2 ${colors.iconBg} rounded`}>
                          <Icon className={`w-4 h-4 ${colors.iconText}`} />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{nodeType.label}</span>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </div>

            {/* Output Nodes */}
            <div className="mb-6">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Output Nodes</h4>
              <div className="space-y-2">
                {nodeTypes.filter(nt => nt.category === 'output').map((nodeType) => {
                  const Icon = nodeType.icon;
                  const colors = colorClasses[nodeType.color as keyof typeof colorClasses];
                  return (
                    <Tooltip key={nodeType.type} content={nodeType.description}>
                      <div
                        draggable
                        onDragStart={() => handleDragStart(nodeType)}
                        className={`flex items-center space-x-3 p-3 ${colors.bg} border ${colors.border} rounded-lg cursor-move hover:shadow-md transition-all`}
                      >
                        <div className={`p-2 ${colors.iconBg} rounded`}>
                          <Icon className={`w-4 h-4 ${colors.iconText}`} />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{nodeType.label}</span>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative">
          <div
            ref={canvasRef}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onMouseMove={handleNodeDrag}
            onMouseUp={handleNodeDragEnd}
            className="w-full h-full bg-gray-50 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] overflow-auto"
            style={{ position: 'relative', cursor: isDragging ? 'grabbing' : 'default' }}
          >
            {/* SVG for connections */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
              {connections.map(renderConnection)}
            </svg>

            {/* Nodes */}
            {nodes.map((node) => {
              const nodeTypeInfo = getNodeTypeInfo(node.type);
              if (!nodeTypeInfo) return null;
              const Icon = nodeTypeInfo.icon;
              const colors = colorClasses[nodeTypeInfo.color as keyof typeof colorClasses];

              return (
                <div
                  key={node.id}
                  className={`absolute bg-white rounded-lg border-2 shadow-lg transition-all cursor-move ${
                    selectedNode?.id === node.id
                      ? 'border-primary-500 shadow-xl'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{
                    left: node.x,
                    top: node.y,
                    width: 150,
                    zIndex: selectedNode?.id === node.id ? 10 : 2,
                  }}
                  onMouseDown={(e) => handleNodeDragStart(node, e)}
                  onClick={() => setSelectedNode(node)}
                >
                  {/* Node Header */}
                  <div className={`px-3 py-2 ${colors.bg} border-b ${colors.border} rounded-t-lg flex items-center justify-between`}>
                    <div className="flex items-center space-x-2">
                      <Icon className={`w-4 h-4 ${colors.iconText}`} />
                      <span className="text-xs font-semibold text-gray-900 truncate">{node.label}</span>
                    </div>
                  </div>

                  {/* Node Body */}
                  <div className="px-3 py-2">
                    <p className="text-xs text-gray-600 truncate">{nodeTypeInfo.description}</p>
                  </div>

                  {/* Connection Points - Increased touch targets to 44x44px */}
                  <div className="absolute -left-5 top-1/2 -translate-y-1/2">
                    <Tooltip content="Input">
                      <div
                        className="w-11 h-11 flex items-center justify-center cursor-pointer"
                        onMouseUp={() => handleConnectionEnd(node.id)}
                      >
                        <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white hover:bg-blue-600 hover:w-5 hover:h-5 transition-all" />
                      </div>
                    </Tooltip>
                  </div>
                  <div className="absolute -right-5 top-1/2 -translate-y-1/2">
                    <Tooltip content="Output">
                      <div
                        className="w-11 h-11 flex items-center justify-center cursor-pointer"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleConnectionStart(node.id);
                        }}
                      >
                        <div className="w-4 h-4 bg-green-500 rounded-full border-2 border-white hover:bg-green-600 hover:w-5 hover:h-5 transition-all" />
                      </div>
                    </Tooltip>
                  </div>

                  {/* Node Actions */}
                  <div className="absolute -top-2 -right-2 flex space-x-1">
                    <Tooltip content="Configure node">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNode(node);
                          setShowNodeConfig(true);
                        }}
                        className="p-1 bg-white rounded-full shadow border border-gray-200 hover:bg-gray-50"
                      >
                        <Settings className="w-3 h-3 text-gray-600" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Delete node">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNode(node.id);
                        }}
                        className="p-1 bg-white rounded-full shadow border border-gray-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              );
            })}

            {/* Empty State */}
            {nodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center max-w-md">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <Zap className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Start Building Your Workflow
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Drag nodes from the left panel and drop them here to create your question generation pipeline
                  </p>
                  <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-blue-500 rounded-full" />
                      <span>Input</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-green-500 rounded-full" />
                      <span>Output</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Panel - Responsive */}
        <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto hidden lg:block">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Workflow Info
            </h3>

            <div className="space-y-4 mb-6">
              <div>
                <p className="text-xs text-gray-500 mb-1">Nodes</p>
                <p className="text-2xl font-bold text-gray-900">{nodes.length}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Connections</p>
                <p className="text-2xl font-bold text-gray-900">{connections.length}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Status</p>
                <StatusBadge status={nodes.length > 0 ? 'active' : 'draft'} />
              </div>
            </div>

            {selectedNode && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Selected Node</h4>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">Type</p>
                    <p className="text-sm font-medium text-gray-900">{selectedNode.label}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">ID</p>
                    <p className="text-xs font-mono text-gray-700">{selectedNode.id}</p>
                  </div>
                  <button
                    onClick={() => setShowNodeConfig(true)}
                    className="w-full btn-primary text-sm"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configure
                  </button>
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 pt-4 mt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Quick Tips</h4>
              <div className="space-y-2 text-xs text-gray-600">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p>Drag nodes from the palette to the canvas</p>
                </div>
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p>Connect nodes by dragging from green dot to blue dot</p>
                </div>
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p>Click nodes to select and configure them</p>
                </div>
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p>Click connections to delete them</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Node Configuration Modal */}
      {showNodeConfig && selectedNode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-primary-50 to-blue-50 border-b border-primary-100 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Settings className="w-6 h-6 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Configure: {selectedNode.label}
                </h3>
              </div>
              <button
                onClick={() => setShowNodeConfig(false)}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {Object.entries(selectedNode.config).map(([key, value]) => {
                  // Special dropdowns for specific fields
                  const dropdownOptions: Record<string, { label: string; value: string }[]> = {
                    questionType: [
                      { label: 'Multiple Choice', value: 'multiple-choice' },
                      { label: 'True/False', value: 'true-false' },
                      { label: 'Essay', value: 'essay' },
                      { label: 'Short Answer', value: 'short-answer' },
                    ],
                    bloomLevel: [
                      { label: 'Remember (Recall facts)', value: 'remember' },
                      { label: 'Understand (Explain ideas)', value: 'understand' },
                      { label: 'Apply (Use knowledge)', value: 'apply' },
                      { label: 'Analyze (Break down info)', value: 'analyze' },
                      { label: 'Evaluate (Make judgments)', value: 'evaluate' },
                      { label: 'Create (Produce new work)', value: 'create' },
                    ],
                    difficulty: [
                      { label: 'Easy', value: 'easy' },
                      { label: 'Medium', value: 'medium' },
                      { label: 'Hard', value: 'hard' },
                    ],
                    examType: [
                      { label: 'CPA-10', value: 'CPA-10' },
                      { label: 'CPA-20', value: 'CPA-20' },
                      { label: 'CEA', value: 'CEA' },
                    ],
                  };

                  return (
                  <div key={key}>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </label>
                    {dropdownOptions[key] ? (
                      <select
                        value={value as string}
                        onChange={(e) => {
                          setNodes(nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, config: { ...n.config, [key]: e.target.value } }
                              : n
                          ));
                          setSelectedNode({
                            ...selectedNode,
                            config: { ...selectedNode.config, [key]: e.target.value }
                          });
                        }}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all appearance-none cursor-pointer"
                      >
                        {dropdownOptions[key].map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : typeof value === 'boolean' ? (
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => {
                          setNodes(nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, config: { ...n.config, [key]: e.target.checked } }
                              : n
                          ));
                          setSelectedNode({
                            ...selectedNode,
                            config: { ...selectedNode.config, [key]: e.target.checked }
                          });
                        }}
                        className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                      />
                    ) : typeof value === 'number' ? (
                      <input
                        type="number"
                        value={value}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          setNodes(nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, config: { ...n.config, [key]: newValue } }
                              : n
                          ));
                          setSelectedNode({
                            ...selectedNode,
                            config: { ...selectedNode.config, [key]: newValue }
                          });
                        }}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      />
                    ) : Array.isArray(value) ? (
                      <textarea
                        value={value.join(', ')}
                        onChange={(e) => {
                          const newValue = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
                          setNodes(nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, config: { ...n.config, [key]: newValue } }
                              : n
                          ));
                          setSelectedNode({
                            ...selectedNode,
                            config: { ...selectedNode.config, [key]: newValue }
                          });
                        }}
                        placeholder="Comma-separated values"
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                        rows={2}
                      />
                    ) : (
                      <input
                        type="text"
                        value={value as string}
                        onChange={(e) => {
                          setNodes(nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, config: { ...n.config, [key]: e.target.value } }
                              : n
                          ));
                          setSelectedNode({
                            ...selectedNode,
                            config: { ...selectedNode.config, [key]: e.target.value }
                          });
                        }}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      />
                    )}
                  </div>
                );
                })}
              </div>

              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={() => setShowNodeConfig(false)}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-orange-50 to-blue-50">
              <div className="flex items-center">
                <BookOpen className="w-6 h-6 text-#ea7500 mr-3" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Workflow Templates</h2>
                  <p className="text-sm text-gray-600 mt-1">Pre-made workflows for all exam types - ready to use or customize</p>
                </div>
              </div>
              <button
                onClick={() => setShowTemplates(false)}
                className="p-2 hover:bg-white rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
              {/* Filter by Exam Type */}
              <div className="mb-6 flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Filter by Exam:</span>
                  <button className="px-3 py-1 text-sm rounded-lg bg-blue-100 text-blue-700 font-medium">All</button>
                  <button className="px-3 py-1 text-sm rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">CPA-10</button>
                  <button className="px-3 py-1 text-sm rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">CPA-20</button>
                  <button className="px-3 py-1 text-sm rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">CEA</button>
                </div>
              </div>

              {/* Templates Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {workflowTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="border border-gray-200 rounded-xl p-5 hover:border-fk-orange-vibrant300 hover:shadow-lg transition-all cursor-pointer bg-white"
                    onClick={() => handleLoadTemplate(template)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{template.name}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{template.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 mt-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-lg ${
                        template.examType === 'CPA-10' ? 'bg-blue-100 text-blue-700' :
                        template.examType === 'CPA-20' ? 'bg-green-100 text-green-700' :
                        'bg-fk-orange-vibrant100 text-#d66a00'
                      }`}>
                        {template.examType}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-700">
                        {template.useCase}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-lg ${
                        template.difficulty === 'beginner' ? 'bg-emerald-100 text-emerald-700' :
                        template.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {template.difficulty.charAt(0).toUpperCase() + template.difficulty.slice(1)}
                      </span>
                    </div>

                    <div className="flex items-center mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center text-xs text-gray-500 mr-4">
                        <Layers className="w-3 h-3 mr-1" />
                        {template.nodes.length} nodes
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <Shuffle className="w-3 h-3 mr-1" />
                        {template.connections.length} connections
                      </div>
                      <div className="ml-auto">
                        <button className="px-3 py-1 text-xs font-medium bg-#ea7500 text-white rounded-lg hover:bg-#d66a00 transition-colors">
                          Load Template
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {workflowTemplates.length === 0 && (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No templates available</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                💡 Tip: Click any template to load it. You can edit all settings after loading.
              </p>
              <button
                onClick={() => setShowTemplates(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-[9999] space-y-2 max-w-md">
        {toasts.map((toast) => {
          const config = {
            success: {
              icon: CheckCircle2,
              bgColor: 'bg-gradient-to-r from-emerald-500 to-green-600',
              iconColor: 'text-white',
            },
            error: {
              icon: XCircle,
              bgColor: 'bg-gradient-to-r from-red-500 to-rose-600',
              iconColor: 'text-white',
            },
            warning: {
              icon: AlertTriangle,
              bgColor: 'bg-gradient-to-r from-amber-500 to-orange-600',
              iconColor: 'text-white',
            },
            info: {
              icon: Info,
              bgColor: 'bg-gradient-to-r from-blue-500 to-#1e73be',
              iconColor: 'text-white',
            },
          };

          const { icon: Icon, bgColor, iconColor } = config[toast.type];

          return (
            <div
              key={toast.id}
              className={`${bgColor} text-white rounded-xl shadow-2xl p-4 pr-12 min-w-[320px] transform transition-all duration-300 ease-out animate-slide-in-right relative`}
              style={{
                animation: 'slideInRight 0.3s ease-out',
              }}
            >
              <div className="flex items-start">
                <div className={`${iconColor} mr-3 mt-0.5`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm mb-0.5">{toast.title}</h4>
                  {toast.message && (
                    <p className="text-xs text-white/90 leading-relaxed">{toast.message}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => dismissToast(toast.id)}
                className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Toast Animation Styles */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

    </div>
  );
}
